import { Button, EmptyState, Menu, type MenuItem } from "@note-app/ui";
import type { KnowledgeItem } from "@note-app/contracts";
import { KnowledgeIcon } from "./KnowledgeIcon.tsx";

export interface KnowledgeItemGridProps {
  readonly items: readonly KnowledgeItem[];
  readonly emptyTitle: string;
  readonly emptyDescription?: string;
  readonly onOpen?: (item: KnowledgeItem) => void;
  readonly onManage?: (item: KnowledgeItem) => void;
  readonly onFavorite?: (item: KnowledgeItem, favorite: boolean) => void;
  readonly onTrash?: (item: KnowledgeItem) => void;
  readonly onRestore?: (item: KnowledgeItem) => void;
  readonly onPurge?: (item: KnowledgeItem) => void;
  readonly busyItemId?: string;
  readonly viewMode?: "grid" | "list";
  readonly selectedIds?: ReadonlySet<string>;
  readonly onSelectionChange?: (item: KnowledgeItem, selected: boolean) => void;
}

const FAVORITE_PROPERTY = "fengyue.favorite";

export function KnowledgeItemGrid({
  items,
  emptyTitle,
  emptyDescription = "快速记录会立即写入本机数据库。",
  onOpen,
  onManage,
  onFavorite,
  onTrash,
  onRestore,
  onPurge,
  busyItemId,
  viewMode = "grid",
  selectedIds,
  onSelectionChange,
}: KnowledgeItemGridProps) {
  if (items.length === 0) {
    return (
      <EmptyState
        title={emptyTitle}
        description={emptyDescription}
        compact
      />
    );
  }
  return (
    <div className={`knowledge-item-grid knowledge-item-grid--${viewMode}`}>
      {items.map((item) => {
        const favorite = item.properties[FAVORITE_PROPERTY] === true;
        const busy = busyItemId === item.id;
        const selected = selectedIds?.has(item.id) ?? false;
        const menuItems: MenuItem[] = [];
        if (onFavorite) {
          menuItems.push({
            id: "favorite",
            label: favorite ? "取消收藏" : "加入收藏",
            icon: <KnowledgeIcon name="star" />,
            disabled: busy,
            onSelect: () => onFavorite(item, !favorite),
          });
        }
        if (onManage) {
          menuItems.push({
            id: "manage",
            label: "整理笔记",
            icon: <KnowledgeIcon name="organize" />,
            disabled: busy,
            onSelect: () => onManage(item),
          });
        }
        if (onTrash) {
          menuItems.push({
            id: "trash",
            label: "移到回收站",
            icon: <KnowledgeIcon name="trash" />,
            disabled: busy,
            tone: "danger",
            onSelect: () => onTrash(item),
          });
        }
        return (
          <article className={`knowledge-item-card${selected ? " is-selected" : ""}`} key={item.id}>
            {onSelectionChange ? (
              <label className="knowledge-item-card__selector" onClick={(event) => event.stopPropagation()}>
                <input type="checkbox" checked={selected} aria-label={`选择 ${item.title}`} onChange={(event) => onSelectionChange(item, event.currentTarget.checked)} />
              </label>
            ) : null}
            <button
              type="button"
              className="knowledge-item-card__open"
              disabled={!onOpen}
              onClick={() => onOpen?.(item)}
            >
              <span className="knowledge-item-grid__icon" aria-hidden="true">
                <KnowledgeIcon name={item.kind === "document" ? "document" : "canvas"} />
              </span>
              <span className="knowledge-item-card__copy">
                <strong>{item.title || "无标题"}</strong>
                <small>
                  {item.kind === "document" ? "文档" : "画板"} ·{" "}
                  {formatRelativeDate(item.updatedAt)}
                </small>
              </span>
              {favorite ? (
                <span className="knowledge-item-card__favorite" title="已收藏">
                  <KnowledgeIcon name="star" />
                  <span className="ui-visually-hidden">已收藏</span>
                </span>
              ) : null}
            </button>
            {onManage || onFavorite || onTrash || onRestore || onPurge ? (
              <footer className="knowledge-item-card__actions">
                {menuItems.length ? (
                  <Menu
                    label={`${item.title} 的更多操作`}
                    items={menuItems}
                    trigger={<KnowledgeIcon name="more" />}
                  />
                ) : <span />}
                {onRestore ? (
                  <Button
                    size="small"
                    variant="ghost"
                    disabled={busy}
                    aria-label={`恢复 ${item.title}`}
                    onClick={() => onRestore(item)}
                  >
                    恢复
                  </Button>
                ) : null}
                {onPurge ? (
                  <Button
                    size="small"
                    variant="danger"
                    disabled={busy}
                    aria-label={`永久删除 ${item.title}`}
                    onClick={() => onPurge(item)}
                  >
                    永久删除
                  </Button>
                ) : null}
              </footer>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}

function formatRelativeDate(timestamp: number): string {
  const delta = Date.now() - timestamp;
  const minutes = Math.max(1, Math.round(delta / 60_000));
  if (minutes < 60) return `${minutes} 分钟前`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} 小时前`;
  return new Intl.DateTimeFormat("zh-CN", {
    month: "short",
    day: "numeric",
  }).format(new Date(timestamp));
}
